import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { vocePrecedente } from "../utils/cronologia";

/**
 * Lo stato di una vista (filtri, ricerca, tab, ordinamento, record aperto)
 * vive nei parametri dell'URL, così che tornando indietro, ricaricando o
 * aprendo un link copiato si ritrovi la stessa vista.
 *
 * Due modi di scrivere, e la scelta non è indifferente:
 * - REPLACE per i filtri: cambiano la voce corrente senza crearne una nuova.
 *   Se ogni tasto premuto nella ricerca fosse una voce, per uscire dalla
 *   pagina servirebbero venti "Indietro".
 * - PUSH per aprire un record: è un passo che "Indietro" deve poter annullare.
 *
 * I nomi dei parametri sono in italiano, come quelli che c'erano già
 * (`stato`, `ritardo`, `servizio`, `mappa`): `ricerca`, `mese`, `anno`, `tab`,
 * `ordina` + `verso`, `open` per il record aperto e `new=1` per il modulo di
 * creazione. Un valore vuoto toglie il parametro invece di lasciarlo a `=`.
 */

export type ValoriParametri = Record<string, string | string[] | null | undefined>;

/**
 * Ricerche digitate e non ancora scritte nell'URL: il timer di `useRicercaUrl`
 * è partito ma non è ancora scattato. Per nome del parametro.
 *
 * Servono a `useScriviParametri`: prima di aprire un pannello (PUSH) la
 * ricerca in attesa va scritta sulla voce dell'elenco, altrimenti il timer
 * scatterebbe dopo e la scriverebbe sulla voce del pannello — e chiudendolo
 * si tornerebbe all'elenco senza la ricerca.
 */
const ricercheInAttesa = new Map<string, { valore: string; annulla: () => void }>();

/** Applica `valori` ai parametri `search` e restituisce la query risultante. */
function applicaValori(search: string, valori: ValoriParametri): string {
  const next = new URLSearchParams(search);
  for (const [nome, valore] of Object.entries(valori)) {
    next.delete(nome);
    if (Array.isArray(valore)) valore.filter(Boolean).forEach((v) => next.append(nome, v));
    else if (valore) next.set(nome, valore);
  }
  return next.toString() ? `?${next.toString()}` : "";
}

/**
 * Scrive più parametri in un colpo solo, partendo dall'URL di QUESTO momento
 * (`window.location`) e non da quello dell'ultimo render: due scritture nello
 * stesso giro, altrimenti, si cancellerebbero a vicenda.
 *
 * Su REPLACE lo `state` della voce si conserva: è la voce di prima, con i
 * filtri aggiornati.
 */
export function useScriviParametri() {
  const navigate = useNavigate();
  return useCallback(
    (valori: ValoriParametri, opzioni: { push?: boolean } = {}) => {
      let attuali = window.location.search;
      const statoVoce = (window.history.state as { usr?: unknown } | null)?.usr;
      // Prima di un PUSH la ricerca ancora in attesa va sulla voce corrente,
      // che è quella a cui si tornerà: timer annullato e REPLACE subito.
      if (opzioni.push && ricercheInAttesa.size > 0) {
        const inAttesa: ValoriParametri = {};
        for (const [nome, { valore, annulla }] of ricercheInAttesa) {
          annulla();
          inAttesa[nome] = valore;
        }
        ricercheInAttesa.clear();
        const conRicerca = applicaValori(attuali, inAttesa);
        if (conRicerca !== attuali) {
          navigate({ pathname: window.location.pathname, search: conRicerca }, { replace: true, state: statoVoce });
          attuali = conRicerca;
        }
      }
      const search = applicaValori(attuali, valori);
      if (search === attuali) return;
      navigate(
        { pathname: window.location.pathname, search },
        opzioni.push ? undefined : { replace: true, state: statoVoce }
      );
    },
    [navigate]
  );
}

/**
 * Campo di ricerca legato a un parametro dell'URL.
 *
 * `testo` è quello che si vede nel campo e cambia a ogni tasto; `valore` è
 * quello nell'URL, scritto 300ms dopo l'ultimo tasto (lo stesso ritardo che le
 * pagine avevano già), ed è quello su cui interrogare il backend. Se l'URL
 * cambia da fuori — Indietro, un link — il campo lo segue.
 */
export function useRicercaUrl(nome = "ricerca", ritardoMs = 300) {
  const [searchParams] = useSearchParams();
  const scrivi = useScriviParametri();
  const valore = (searchParams.get(nome) ?? "").trim();
  const [testo, setTesto] = useState(valore);

  useEffect(() => {
    setTesto((t) => (t.trim() === valore ? t : valore));
  }, [valore]);

  useEffect(() => {
    if (testo.trim() === valore) return;
    const voce = {
      valore: testo.trim(),
      annulla: () => clearTimeout(t),
    };
    const t = setTimeout(() => {
      if (ricercheInAttesa.get(nome) === voce) ricercheInAttesa.delete(nome);
      scrivi({ [nome]: voce.valore });
    }, ritardoMs);
    ricercheInAttesa.set(nome, voce);
    return () => {
      clearTimeout(t);
      if (ricercheInAttesa.get(nome) === voce) ricercheInAttesa.delete(nome);
    };
    // `valore` fuori di proposito: quando l'URL raggiunge il testo non c'è
    // niente da riscrivere, e quando l'URL cambia da fuori è l'effetto sopra a
    // riallineare il testo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testo, nome, ritardoMs, scrivi]);

  return { testo, setTesto, valore };
}

/** `?mese=1..12` per le tendine `MeseSelect`; altro = tutti i mesi. */
export function meseDaUrl(searchParams: URLSearchParams): number | undefined {
  const m = Number(searchParams.get("mese"));
  return Number.isInteger(m) && m >= 1 && m <= 12 ? m : undefined;
}

export interface OrdineTabella {
  key: string;
  verso: "asc" | "desc";
}

/**
 * Ordinamento di una `Table` nell'URL: `?ordina=<colonna>&verso=desc`. Senza
 * `verso` vale crescente. Una colonna che la tabella non conosce viene
 * ignorata da lei, quindi un link vecchio non rompe niente.
 */
export function useOrdinamentoUrl() {
  const [searchParams] = useSearchParams();
  const scrivi = useScriviParametri();
  const key = searchParams.get("ordina");
  const ordine: OrdineTabella | null = key
    ? { key, verso: searchParams.get("verso") === "desc" ? "desc" : "asc" }
    : null;
  const onOrdineChange = useCallback(
    (o: OrdineTabella | null) => scrivi({ ordina: o?.key, verso: o?.verso === "desc" ? "desc" : null }),
    [scrivi]
  );
  return { ordine, onOrdineChange };
}

/**
 * Pannello laterale (o scheda) aperto via URL: `?open=<id>` per un record,
 * `?new=1` per il modulo di creazione.
 *
 * Aprire è un PUSH. Chiudere dipende da come ci si è arrivati: se la voce
 * prima è la stessa pagina senza pannello — cioè lo si è aperto da qui — si
 * torna indietro, e la cronologia resta com'era prima di aprirlo; se invece si
 * è arrivati con il pannello già aperto (dal Calendario, dalla Dashboard, da un
 * link) si toglie il parametro con un REPLACE, senza uscire dalla pagina.
 *
 * `chiaviCollegate` sono i parametri che accompagnano l'apertura e vanno via
 * con lei (es. `schedule` in `?new=1&schedule=<id>`).
 */
export function usePannelloUrl(chiaviCollegate: string[] = []) {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const scrivi = useScriviParametri();
  const apertoId = searchParams.get("open");
  const nuovo = searchParams.get("new") === "1";

  // Due chiusure sulla stessa voce (il pulsante e l'Esc, o il salvataggio e
  // l'`onClose` del pannello) farebbero due passi indietro invece di uno:
  // `navigate(-1)` non cambia la location subito, e il secondo partirebbe
  // dalla stessa voce.
  const chiusaSuVoce = useRef<string | null>(null);

  // Il guard vale solo per la navigazione in corso: appena la location cambia
  // voce si azzera. Senza, dopo una chiusura con `navigate(-1)` restava legato
  // alla key di quella voce, e tornandoci con Avanti (React Router ridà la
  // stessa key) il pannello si riapriva ma X, Annulla e il salvataggio non lo
  // chiudevano più.
  useEffect(() => {
    if (chiusaSuVoce.current !== location.key) chiusaSuVoce.current = null;
  }, [location.key]);

  const apri = useCallback((id: string) => scrivi({ open: id, new: null }, { push: true }), [scrivi]);

  const apriNuovo = useCallback(
    (extra: ValoriParametri = {}) => scrivi({ new: "1", open: null, ...extra }, { push: true }),
    [scrivi]
  );

  const collegate = chiaviCollegate.join(",");
  const chiudi = useCallback(() => {
    const qui = new URLSearchParams(window.location.search);
    if (!qui.has("open") && !qui.has("new") && !chiaviCollegate.some((k) => qui.has(k))) return;
    if (chiusaSuVoce.current === location.key) return;
    chiusaSuVoce.current = location.key;

    const prec = vocePrecedente();
    const precParams = prec ? new URLSearchParams(prec.search) : null;
    if (prec && precParams && prec.pathname === window.location.pathname && !precParams.has("open") && !precParams.has("new")) {
      navigate(-1);
      return;
    }
    scrivi({ open: null, new: null, ...Object.fromEntries(chiaviCollegate.map((k) => [k, null])) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, navigate, scrivi, collegate]);

  return { apertoId, nuovo, apri, apriNuovo, chiudi };
}

/** Segnaposto di `pronto` per il modulo di creazione: non è l'id di nessuno. */
const PRONTO_NUOVO = "\u0000nuovo";

/**
 * `usePannelloUrl` per i pannelli che mostrano UN record dell'elenco della
 * pagina, con un modulo da preparare (react-hook-form `reset`, stato del
 * censimento…) prima di farlo vedere.
 *
 * Il pannello si apre quando l'URL lo chiede E la preparazione è fatta, non
 * prima: altrimenti per un istante si vedrebbe il modulo del record di prima.
 * Un `?open=<id>` che non è fra i record caricati si toglie dall'URL, come
 * facevano le pagine prima di questo hook — ma solo dopo che sulla voce
 * corrente è finito un caricamento dell'elenco: vedi `caricatoSuVoce`.
 */
export function usePannelloRecord<T extends { id: string }>(opzioni: {
  records: T[];
  isLoading: boolean;
  preparaNuovo: () => void;
  preparaModifica: (record: T) => void;
  chiaviCollegate?: string[];
}) {
  const pannello = usePannelloUrl(opzioni.chiaviCollegate);
  const location = useLocation();
  const [pronto, setPronto] = useState<string | null>(null);
  // Le due funzioni cambiano identità a ogni render della pagina: si leggono
  // dal ref, perché l'effetto deve ripartire quando cambia l'URL o arrivano i
  // record, non a ogni render.
  const prepara = useRef(opzioni);
  prepara.current = opzioni;

  const { records, isLoading } = opzioni;

  // I record in memoria sono quelli dell'ultimo caricamento, che può essere di
  // un'altra voce. Con Avanti su `?ricerca=A&open=<id>` dopo aver cercato B,
  // al primo giro l'elenco è ancora quello di B, già caricato: se il record
  // non c'è, togliere `?open=` lì riporterebbe subito a B prima che parta la
  // richiesta per A. Quindi un record assente fa chiudere solo dopo che su
  // QUESTA voce un caricamento è iniziato ed è finito. Un record già presente
  // in memoria, invece, si apre subito.
  const caricamento = useRef<{ inCorso: boolean; inizioSu: string | null; finitoSu: string | null }>({
    inCorso: false,
    inizioSu: null,
    finitoSu: null,
  });
  if (isLoading !== caricamento.current.inCorso) {
    caricamento.current = isLoading
      ? { inCorso: true, inizioSu: location.key, finitoSu: caricamento.current.finitoSu }
      : {
          inCorso: false,
          inizioSu: null,
          finitoSu: caricamento.current.inizioSu === location.key ? location.key : caricamento.current.finitoSu,
        };
  }
  const caricatoSuVoce = caricamento.current.finitoSu === location.key;

  useEffect(() => {
    if (pannello.nuovo) {
      if (pronto !== PRONTO_NUOVO) {
        prepara.current.preparaNuovo();
        setPronto(PRONTO_NUOVO);
      }
      return;
    }
    const id = pannello.apertoId;
    if (!id) {
      if (pronto !== null) setPronto(null);
      return;
    }
    if (pronto === id || isLoading) return;
    const record = records.find((r) => r.id === id);
    if (record) {
      prepara.current.preparaModifica(record);
      setPronto(id);
    } else if (caricatoSuVoce && records.length > 0) {
      // Solo con un elenco caricato su questa voce e con righe, come prima di
      // questo hook: un elenco vuoto può essere un caricamento fallito, e
      // togliere qui `?open=` farebbe perdere il link — riprovando o
      // ricaricando, il record non si aprirebbe più.
      pannello.chiudi();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pannello.nuovo, pannello.apertoId, records, isLoading, caricatoSuVoce]);

  /**
   * Il record appena creato resta aperto nel pannello (Sopralluoghi: per
   * allegare subito le foto). `?new=1` diventa `?open=<id>` sulla stessa voce,
   * senza ripreparare il modulo: l'utente resta dov'era.
   */
  const scrivi = useScriviParametri();
  const chiaviCollegate = opzioni.chiaviCollegate;
  const passaAModifica = useCallback(
    (id: string) => {
      setPronto(id);
      scrivi({ new: null, open: id, ...Object.fromEntries((chiaviCollegate ?? []).map((k) => [k, null])) });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scrivi, (chiaviCollegate ?? []).join(",")]
  );

  const apertoNuovo = pannello.nuovo && pronto === PRONTO_NUOVO;
  const apertoModifica = !pannello.nuovo && pannello.apertoId !== null && pronto === pannello.apertoId;
  return { ...pannello, apertoNuovo, apertoModifica, aperto: apertoNuovo || apertoModifica, passaAModifica };
}

/**
 * "Indietro" verso la rotta padre (`/devices`, `/clients/<id>`…).
 *
 * Se la voce precedente è proprio quella pagina, si torna indietro e si
 * ritrovano filtri, ricerca e scroll com'erano. Altrimenti — link incollato,
 * arrivo da un'altra sezione — si naviga alla rotta padre: l'etichetta del
 * pulsante dice dove porta, e deve continuare a dire il vero.
 */
export function useTornaIndietro() {
  const navigate = useNavigate();
  return useCallback(
    (padre: string, opzioni: { replace?: boolean } = {}) => {
      const prec = vocePrecedente();
      const percorsoPadre = padre.split("?")[0];
      if (prec && prec.pathname === percorsoPadre) navigate(-1);
      else navigate(padre, { replace: opzioni.replace });
    },
    [navigate]
  );
}
